import React, { useMemo } from 'react';
import {
  formatRatingAverage,
  RATING_STAR_COUNT,
  RATING_STAR_PATH,
  starFillFraction
} from './ratingStars';

interface RatingStarsViewProps {
  average: number;
  className?: string;
}

/**
 * Read-only gold stars for a fixed average (e.g. best-take summary on song detail).
 */
const RatingStarsView: React.FC<RatingStarsViewProps> = ({ average, className }) => {
  const displayAverage = Number.isFinite(average) ? Number(average.toFixed(1)) : 0;
  const averageLabel = formatRatingAverage(displayAverage, 1);

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-rating-results', 'rich-text-rating-stars'];
    if (className) classes.push(className);
    return classes.join(' ');
  }, [className]);

  const stars = Array.from({ length: RATING_STAR_COUNT }, (_, index) => {
    const starIndex = index + 1;
    const fill = starFillFraction(displayAverage, starIndex);
    const pct = Math.round(fill * 1000) / 10;
    return (
      <span
        key={starIndex}
        className="rich-text-rating-results__star"
        data-star-index={starIndex}
        data-fill={String(fill)}
        aria-hidden="true"
      >
        <svg
          className="rich-text-rating-results__star-empty"
          viewBox="0 0 24 24"
          focusable="false"
        >
          <path d={RATING_STAR_PATH} />
        </svg>
        {pct > 0 && (
          <span
            className="rich-text-rating-results__star-fill"
            style={{ width: `${pct}%` }}
          >
            <svg viewBox="0 0 24 24" focusable="false">
              <path d={RATING_STAR_PATH} />
            </svg>
          </span>
        )}
      </span>
    );
  });

  return (
    <div
      className={rootClassName}
      data-testid="rating-stars"
      data-average={averageLabel}
      role="img"
      aria-label={`${averageLabel} out of 5 stars`}
    >
      <div className="rich-text-rating-results__summary">
        <div className="rich-text-rating-results__stars">{stars}</div>
      </div>
    </div>
  );
};

export default RatingStarsView;
